import { z } from 'zod';
import { API_BASE_URL } from '~/config';

export const validResolutions = [
  { width: 1024, height: 1024 },
  { width: 1152, height: 896 },
  { width: 896, height: 1152 },
  { width: 1216, height: 832 },
  { width: 832, height: 1216 },
  { width: 1344, height: 768 },
  { width: 768, height: 1344 },
  { width: 1536, height: 640 },
  { width: 640, height: 1536 },
];

export const ImageGenerationOptionsSchema = z
  .object({
    // TODO move this out so it need not be special cased
    prompt: z.string().describe('Prompt to use for the image generation process.'),
    negative_prompt: z
      .string()
      .optional()
      .describe('Negative prompt to influence the image generation away from certain elements.'),
    accept: z.enum(['image/jpeg', 'image/png']).optional().describe('Format of the generated image.'),
    width: z
      .union([
        z.literal(640),
        z.literal(768),
        z.literal(832),
        z.literal(896),
        z.literal(1024),
        z.literal(1152),
        z.literal(1216),
        z.literal(1344),
        z.literal(1536),
      ])
      .optional()
      .describe(
        'Width of the image in pixels. Supported widths are 1024, 1152, 1216, 1344, and 1536, corresponding to supported resolutions.',
      ),
    height: z
      .union([
        z.literal(640),
        z.literal(768),
        z.literal(832),
        z.literal(896),
        z.literal(1024),
        z.literal(1152),
        z.literal(1216),
        z.literal(1344),
        z.literal(1536),
      ])
      .optional()
      .describe(
        'Height of the image in pixels. Supported heights are 1024, 896, 832, 768, and 640, corresponding to supported resolutions.',
      ),

    cfg_scale: z.number().optional().describe('Classifier-free guidance scale for the image diffusion process.'),
    seed: z
      .number()
      .optional()
      .describe('Random seed to use for the image generation process. If 0, a totally random seed will be used.'),
    steps: z.number().min(1).optional().describe('Number of steps to use for the image generation process.'),
    sampler: z.enum(['K_EULER', 'K_DPMPP_2M']).optional().describe('Sampler to use for the image generation process.'),
    safety_check: z.boolean().optional().describe('Enable a safety check for each response to filter unsafe content.'),
  })
  .describe('Options for generating an image from a text prompt.')
  .superRefine((data, ctx) => {
    const resolutionIsValid = validResolutions.some(
      (resolution) => resolution.width === data.width && resolution.height === data.height,
    );

    if (data.width && data.height && !resolutionIsValid) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Invalid resolution. Supported resolutions are 1024x1024, 1152x896, 896x1152, 1216x832, 832x1216, 1344x768, 768x1344, 1536x640, and 640x1536.',
        path: ['width', 'height'],
      });
    }
  });
export type ImageGenerationOptions = z.infer<typeof ImageGenerationOptionsSchema>;

export const imageTransformationOptionsSchema = z
  .object({
    // TODO move this out so it need not be special cased
    prompt: z.string().describe('Prompt to use for the image transformation process.'),
    negative_prompt: z.string().optional().describe('Negative prompt to avoid certain elements in the transformation.'),
    accept: z.enum(['image/jpeg', 'image/png']).optional().describe('Format of the generated image.'),
    init_image_mode: z
      .union([z.literal('IMAGE_STRENGTH'), z.literal('STEP_SCHEDULE')])
      .optional()
      .describe('Mode to run the transformation process in.'),
    image_strength: z.number().min(0).max(1).optional().describe('Fraction of steps to use image context.'),

    step_schedule_start: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe('Fraction of steps at the beginning to use init_image context.'),
    step_schedule_end: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe('Fraction of steps at the end to use init_image context.'),

    cfg_scale: z.number().optional().describe('Classifier-free guidance scale for the image diffusion process.'),
    sampler: z.enum(['K_EULER', 'K_DPMPP_2M']).optional().describe('Sampler to use for the image generation process.'),
    // TODO UI is needed for this
    // num_images: z.number().min(1).max(2).optional().describe('Number of images to generate.'),
    seed: z
      .number()
      .optional()
      .describe('Random seed to use for the image generation process. If 0, a totally random seed will be used.'),
    steps: z.number().min(1).max(100).optional().describe('Number of steps to use for the image generation process.'),
    safety_check: z.boolean().optional().describe('Enable a safety check for each response to filter unsafe content.'),
  })
  .describe('Options for transforming an image based on a text prompt.');
export type ImageTransformationOptions = z.infer<typeof imageTransformationOptionsSchema>;

export const minimalImageTransformationSchema = imageTransformationOptionsSchema.omit({
  image_strength: true,
  step_schedule_start: true,
  step_schedule_end: true,
});
export type MinimalImageTransformationOptions = z.infer<typeof imageTransformationOptionsSchema>;

export const imageTransformationWithStepsSchema = imageTransformationOptionsSchema.omit({ image_strength: true });
export type ImageTransformationWithStepsOptions = z.infer<typeof imageTransformationWithStepsSchema>;

export const imageTransformationWithImageStrengthSchema = imageTransformationOptionsSchema.omit({
  step_schedule_start: true,
  step_schedule_end: true,
});
export type ImageTransformationWithImageStrengthOptions = z.infer<typeof imageTransformationWithImageStrengthSchema>;

export const ControlNetOptionsSchema = z.object({
  // TODO move this out so it need not be special cased
  prompt: z.string().describe('Prompt to use for the image generation process.'),
  accept: z.enum(['image/jpeg', 'image/png']).optional().describe('Format of the generated image.'),
  control_net_name: z.enum(['canny', 'qr']).optional().describe('Which ControlNet to use.'),
  conditioning_scale: z
    .number()
    .min(0)
    .max(1)
    .optional()
    .describe('Classifier-free guidance scale for the image diffusion process.'),
  step_schedule_start: z
    .number()
    .min(0)
    .max(1)
    .optional()
    .describe('Fraction of steps at the beginning to use init_image context.'),
  step_schedule_end: z
    .number()
    .min(0)
    .max(1)
    .optional()
    .describe('Fraction of steps at the end to use init_image context.'),
  cfg_scale: z.number().optional().describe('Classifier-free guidance scale for the image diffusion process.'),
  sampler: z.enum(['K_EULER', 'K_DPMPP_2M']).optional().describe('Sampler to use for the image generation process.'),
  seed: z
    .number()
    .optional()
    .describe('Random seed to use for the image generation process. If 0, a totally random seed will be used.'),
  steps: z.number().min(1).max(100).optional().describe('Number of steps to use for the image generation process.'),
  safety_check: z.boolean().optional().describe('Enable a safety check for each response to filter unsafe content.'),
});
export type ControlNetOptions = z.infer<typeof ControlNetOptionsSchema>;

export const CannyEdgeDetectionOptionsSchema = z.object({
  accept: z.enum(['image/jpeg', 'image/png']).optional().describe('Format of the generated image.'),
  min_val: z.number().min(0).max(255).optional().describe('Minimum threshold for edge detection.'),
  max_val: z.number().min(0).max(255).optional().describe('Maximum threshold for edge detection.'),
});
export type CannyEdgeDetectionOptions = z.infer<typeof CannyEdgeDetectionOptionsSchema>;

export const QrCodeOptionsSchema = z.object({
  prompt: z.string().describe('Prompt to use for the QR code generation process.'),
  accept: z.enum(['image/jpeg', 'image/png']).optional().describe('Format of the generated image.'),
  width: z.number().min(0).max(1024).optional().describe('Width of the generated QR code.'),
  height: z.number().min(0).max(1024).optional().describe('Height of the generated QR code.'),
});
export type QrCodeOptions = z.infer<typeof QrCodeOptionsSchema>;

/*** SD3 ***/
export const Sd3BaseOptionsSchema = z.object({
  model: z.enum(['sd3', 'sd3-turbo']).optional().describe('SD3 model used to generate images.'),
  mode: z.enum(['text-to-image', 'image-to-image']).describe('Image generation mode.'),
  prompt: z.string().describe('Prompt to use for the image generation process.'),
  negative_prompt: z
    .string()
    .optional()
    .describe('Negative prompt to influence the image generation away from certain elements.'),
  authorization: z.string().describe('Enter a Stability API key, not a Fireworks API key.'),
  seed: z
    .number()
    .min(0)
    .max(4294967294)
    .optional()
    .describe('Random seed to use for the image generation process. If 0, a totally random seed will be used.'),
  output_format: z.enum(['jpeg', 'png']).optional().describe('Format of the generated image.'), // also webp
  // accept: z.enum(['image/jpeg', 'image/png']).optional().describe('Format of the generated image.'),
});
export type Sd3BaseOptions = z.infer<typeof Sd3BaseOptionsSchema>;

export const Sd3TextToImageOptionsSchema = Sd3BaseOptionsSchema.merge(
  z.object({
    aspect_ratio: z
      .enum(['16:9', '1:1', '21:9', '2:3', '3:2', '4:5', '5:4', '9:16', '9:21'])
      .optional()
      .describe('Aspect ratio of the generated image.'),
  }),
).superRefine((data, ctx) => {
  if (!data.authorization) {
    ctx.addIssue({
      code: 'custom',
      message: 'Stability API Key is required to generate the image',
      path: ['authorization'],
    });
  }
});
export type Sd3TextToImageOptions = z.infer<typeof Sd3TextToImageOptionsSchema>;

export const Sd3ImageToImageOptionsSchema = Sd3BaseOptionsSchema.merge(
  z.object({
    strength: z
      .number()
      .min(0)
      .max(1)
      .optional()
      .describe('Amount of influence the source image has on the diffusion process.'),
  }),
).superRefine((data, ctx) => {
  if (!data.authorization) {
    ctx.addIssue({
      code: 'custom',
      message: 'Stability API Key is required to generate the image',
      path: ['authorization'],
    });
  }
});
export type Sd3ImageToImageOptions = z.infer<typeof Sd3ImageToImageOptionsSchema>;

export class FireworksImageSDK {
  private readonly baseUrl: string = `${API_BASE_URL}/inference/v1/image_generation`;
  private readonly authToken: string;

  public constructor(authToken: string) {
    this.authToken = authToken;
  }

  private getAuthHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${this.authToken}`,
    };
  }

  private async makeRequest(
    url: string,
    method: string,
    headers: HeadersInit,
    body?: FormData | string,
  ): Promise<Response> {
    const requestOptions: RequestInit = {
      method,
      headers,
      body,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 100_000); // Timeout set to 100 seconds
    const response = await fetch(url, { ...requestOptions, signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        // Assuming the response is in text format. Use response.json() if it's in JSON format.
        const responseBody = await response.text();
        errorMessage += `; body: ${responseBody}`;
      } catch {
        errorMessage += '; error reading response body';
      }
      throw new Error(errorMessage);
    }
    return response;
  }

  private createImageFormData(
    image: {
      key: string;
      value: Blob;
    },
    additionalParams: Record<string, any>,
  ): FormData {
    const formData = new FormData();
    formData.append(image.key, image.value, 'image.jpg');

    for (const [key, value] of Object.entries(additionalParams)) {
      if (value !== null && value !== undefined) formData.append(key, value.toString());
    }

    return formData;
  }

  public async generateImage(modelId: string, options: ImageGenerationOptions): Promise<any> {
    const validationResult = ImageGenerationOptionsSchema.safeParse(options);
    if (!validationResult.success) {
      throw new Error(
        `Pre-request API validation failed: ${validationResult.error.issues.map((issue) => issue.message).join(', ')}`,
      );
    }
    const { accept, sampler, ...params } = validationResult.data;

    const url = `${this.baseUrl}/${modelId}`;
    const headers = this.getAuthHeaders();
    const body = JSON.stringify({
      model: modelId.split('/').pop(),
      sampler: sampler ?? null,
      ...params,
    });

    return await this.makeRequest(
      url,
      'POST',
      { ...headers, 'Content-Type': 'application/json', Accept: accept ?? 'image/jpeg' },
      body,
    );
  }

  public async transformImage(modelId: string, image: Blob, options: ImageTransformationOptions): Promise<any> {
    const validationResult = imageTransformationOptionsSchema.safeParse(options);
    if (!validationResult.success) {
      throw new Error(
        `Pre-request API validation failed: ${validationResult.error.issues.map((issue) => issue.message).join(', ')}`,
      );
    }
    const { accept, sampler, ...params } = validationResult.data;

    const formData = this.createImageFormData(
      {
        key: 'init_image',
        value: image,
      },
      {
        sampler: sampler ?? null,
        ...params,
      },
    );

    const url = `${this.baseUrl}/${modelId}/image_to_image`;
    const headers = this.getAuthHeaders(); // Do not set Content-Type manually for FormData

    return await this.makeRequest(url, 'POST', { ...headers, Accept: accept ?? 'image/jpeg' }, formData);
  }

  public async controlNet(modelId: string, image: Blob, options: ControlNetOptions): Promise<any> {
    const validationResult = ControlNetOptionsSchema.safeParse(options);
    if (!validationResult.success) {
      throw new Error(
        `Pre-request API validation failed: ${validationResult.error.issues.map((issue) => issue.message).join(', ')}`,
      );
    }
    const { accept, sampler, ...params } = validationResult.data;

    const formData = this.createImageFormData(
      {
        key: 'control_image',
        value: image,
      },
      {
        sampler: sampler ?? null,
        ...params,
      },
    );
    const url = `${this.baseUrl}/${modelId}/control_net`;
    const headers = this.getAuthHeaders(); // Do not set Content-Type manually for FormData

    return await this.makeRequest(url, 'POST', { ...headers, Accept: accept ?? 'image/jpeg' }, formData);
  }

  public async cannyEdgeDetection(modelId: string, image: Blob, options: CannyEdgeDetectionOptions): Promise<any> {
    const validationResult = CannyEdgeDetectionOptionsSchema.safeParse(options);
    if (!validationResult.success) {
      throw new Error(
        `Pre-request API validation failed: ${validationResult.error.issues.map((issue) => issue.message).join(', ')}`,
      );
    }

    const { accept, min_val, max_val } = validationResult.data;

    const formData = this.createImageFormData(
      {
        key: 'image',
        value: image,
      },
      {
        // min_val,
        // max_val,
      },
    );
    const url = `${this.baseUrl}/${modelId}/canny_edge_detection`;
    const headers = this.getAuthHeaders(); // Do not set Content-Type manually for FormData

    return await this.makeRequest(url, 'POST', { ...headers, Accept: accept ?? 'image/jpeg' }, formData);
  }

  public async qrCode(modelId: string, options: QrCodeOptions): Promise<any> {
    const validationResult = QrCodeOptionsSchema.safeParse(options);
    if (!validationResult.success) {
      throw new Error(
        `Pre-request API validation failed: ${validationResult.error.issues.map((issue) => issue.message).join(', ')}`,
      );
    }

    const { prompt, accept, width, height } = validationResult.data;

    const url = `${this.baseUrl}/${modelId}/qr_code`;
    const headers = this.getAuthHeaders();

    return await this.makeRequest(
      url,
      'POST',
      {
        ...headers,
        'Content-Type': 'application/json',
        Accept: accept ?? 'image/jpeg',
        // width: String(width),
        // height: String(height),
      },
      JSON.stringify({
        prompt,
      }),
    );
  }
}
